import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { Dropdown } from 'pages/AlertsPage/AlertsTab/Dropdown';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { themeColors } from 'themes';
import { Tab } from 'types/maintenance';

interface TabProps {
  tabs?: Tab[];
  selectedTab?: Tab;
  onTabClick: (subTab: Tab, mainTab: Tab) => void;
  selectedMainTab?: Tab;
  selectedSubTab?: Tab;
}
const NavSubMenu = styled.div`
  flex-direction: column;
  list-style: none;
  position: absolute;
  top: 3rem;
  left: 0.5rem;
  right: 0;
  background: #ffffff;
  padding: 0.5rem 0;
  // border-radius: 0.313rem;
  border-top-left-radius: 0rem;
  border-top-right-radius: 0rem;
  z-index: 101;
  // box-shadow: rgba(0, 0, 0, 0.1) 0rem 0.438rem 0.563rem 0rem;
  min-width: 10rem;
  width: fit-content;
  border-radius: 0.625rem;
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.12);
`;

const MainTab = styled.div<{ isSelected?: boolean; isVisible?: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  padding: 0 0.75rem 0.25rem;
  color: ${(props) => (props.isSelected ? themeColors.primaryBlue5 : themeColors.lightGrey8)};
  border: 0;
  font-size: 0.875rem;
  font-weight: ${(props) => (props.isSelected ? '600' : '400')};
  cursor: pointer;
  position: relative;
  border-bottom: ${(props) =>
    props.isSelected ? `4px solid ${props.theme.colors.primaryBlue5}` : ''};
  font-family: Roboto;
  font-style: normal;
`;
const SubText = styled.div<{ padding?: string }>`
  margin: 0.3rem auto 0;
  text-align: center;
  // font-size: 0.8rem;
  font-weight: 400;
  padding: ${({ padding }) => padding};
  font-size: 0.75rem;
`;
const SubTab = styled.li<{ isSelected?: boolean; isVisible?: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  color: ${(props) => (props.isSelected ? themeColors.primaryBlue5 : themeColors.textLightBlack)};
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-weight: ${(props) => (props.isSelected ? 500 : 400)};
  min-width: fit-content;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 120%;
`;
const TabContainer = styled.div`
  display: flex;
  // gap: 4rem;
  user-select: none;
  box-shadow: 0px 1px 1px 0px ${(props) => props.theme.colors.lightBlack};
`;
const DropdownTab = ({
  tabs = [],
  onTabClick,
  selectedTab,
  selectedMainTab,
  selectedSubTab
}: TabProps): JSX.Element => {
  const activeMainTab = tabs.find(
    (tab) => tab.subTabs?.some((subTab) => subTab.selected) || tab.selected
  );
  const activeSubTab = activeMainTab?.subTabs?.find((subTab) => subTab.selected);

  const [currentTab, setCurrentTab] = useState<Tab>();
  const [activeTab, setActiveTab] = useState<{ subTab?: Tab; mainTab?: Tab }>({
    mainTab: selectedMainTab || activeMainTab || tabs[0],
    subTab: selectedSubTab || activeSubTab
  });
  const [showSubTab, setShowSubTab] = useState<boolean>();
  const { t } = useTranslation(['fpns']);

  const { ref } = useComponentVisible(setShowSubTab);

  const handleTabClick = (tab: Tab) => {
    setCurrentTab(tab);
    setShowSubTab(!showSubTab);
    if (!tab?.subTabs?.length) {
      onTabClick(tab, tab);
      setActiveTab({ ...activeTab, mainTab: tab });
    }
  };

  const handleSubTabClick = (subTab: Tab, mainTab: Tab) => {
    setActiveTab({ subTab, mainTab });
    onTabClick(subTab, mainTab);
  };

  useEffect(() => {
    setActiveTab({
      mainTab: selectedMainTab || activeTab.mainTab,
      subTab: selectedSubTab || activeTab.subTab
    });
  }, [selectedMainTab, selectedSubTab]);

  return (
    <div>
      <TabContainer ref={ref}>
        {tabs.map((mainTab) => {
          const isSelected = mainTab.label === activeTab?.mainTab?.label;
          const isCurrentTab = mainTab.label === currentTab?.label;
          return (
            <MainTab
              isSelected={isSelected}
              key={mainTab.label}
              onClick={() => handleTabClick(mainTab)}
              isVisible={mainTab.isVisible === false ? false : true}
            >
              {mainTab.label} &nbsp;
              {isSelected && (
                <FontAwesomeIcon
                  className="icon"
                  color={themeColors.primaryBlue5}
                  icon={showSubTab && isCurrentTab ? faChevronUp : faChevronDown}
                />
              )}
              <br />
              {isSelected && mainTab?.subTabs ? (
                <SubText>({t(activeTab?.subTab?.label || '')})</SubText>
              ) : (
                <SubText padding=".2rem"></SubText>
              )}
              {isCurrentTab && showSubTab && mainTab.subTabs && (
                <NavSubMenu>
                  {mainTab.subTabs?.map((subTab) => {
                    return (
                      <SubTab
                        key={subTab.label}
                        onClick={() => {
                          handleSubTabClick(subTab, mainTab);
                        }}
                        isSelected={subTab.label === activeTab?.subTab?.label}
                        isVisible={subTab.isVisible === false ? false : true}
                      >
                        <a>{t(subTab.label) as string}</a>
                      </SubTab>
                    );
                  })}
                </NavSubMenu>
              )}
            </MainTab>
          );
        })}
      </TabContainer>
    </div>
  );
};

export default React.memo(DropdownTab);

export function useComponentVisible(
  setShowContent: React.Dispatch<React.SetStateAction<boolean | undefined>>
): {
  ref:
    | ((instance: HTMLDivElement | null) => void)
    | React.MutableRefObject<HTMLDivElement>
    | null
    | undefined;
} {
  /*eslint-disable-next-line*/
  const ref = useRef<any>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current?.contains(event.target)) {
      setShowContent(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  return { ref };
}
